import { ScanSearch, Cog, ShieldPlus, Workflow, Globe, Smartphone, FileText, Check } from "lucide-react";
import { useFlowReveal } from "@/hooks/useFlowReveal";

const services = [
  {
    icon: ScanSearch,
    title: "Web Accessibility Audit & WCAG Audit Services",
    description: "Comprehensive WCAG compliance India solution powered by our automated accessibility testing tool and validated by IAAP CPACC-certified experts.",
    features: ["AI Accessibility Testing + Manual Review", "Detailed Website Accessibility Compliance Reports", "Issue Severity & WCAG Mapping"],
  },
  {
    icon: Cog,
    title: "Accessibility Remediation Services",
    description: "End-to-end accessibility remediation services — from accessibility compliance software integration to hands-on fixes.",
    features: ["Code-level issue resolution", "UI/UX accessibility improvements", "PDF accessibility remediation", "Mobile app accessibility testing & fixes"],
  },
  {
    icon: ShieldPlus,
    title: "VPAT, ADA & Section 508 Compliance Services",
    description: "ADA website compliance, Section 508 compliance services, European Accessibility Act compliance, and VPAT reports — all in one engagement.",
    features: ["Accessibility Conformance Reports (ACR / VPAT)", "Accessibility monitoring software integration", "Release-level accessibility checks", "Governance & best practices"],
  },
  {
    icon: Workflow,
    title: "Accessibility Consultant India — Enterprise Solutions",
    description: "Enterprise WCAG solutions embedded at the core of your product lifecycle by a trusted accessibility consultant in India.",
    features: ["Accessible Design Systems — Standardized, compliant UI components", "Team Enablement — Hands-on training for developers and product teams", "Shift-Left Testing — Accessibility checks integrated into CI/CD workflows"],
  },
];

const platforms = [
  { icon: Globe, label: "Websites & Web Applications — Website Accessibility Audit" },
  { icon: Smartphone, label: "Mobile Apps (iOS & Android) — Mobile App Accessibility Testing" },
  { icon: FileText, label: "PDFs & Digital Documents — PDF Accessibility Services" },
];

const whyMatters = [
  "Meet legal and regulatory requirements",
  "Improve usability for all users",
  "Expand your customer reach",
  "Strengthen brand reputation",
  "Reduce long-term rework costs",
];

const ServicesSection = () => {
  const flowRef = useFlowReveal<HTMLDivElement>();

  return (
  <section id="services" className="section-padding" aria-labelledby="services-heading">
    <div className="max-w-7xl mx-auto">
      <p className="eyebrow mb-4">What We Do</p>
      <h2 id="services-heading" className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-8 max-w-3xl">
        Website Accessibility Services &amp; Enterprise <abbr title="Web Content Accessibility Guidelines">WCAG</abbr> Solutions
      </h2>

      <div ref={flowRef} className="grid md:grid-cols-2 gap-x-14 gap-y-12 mb-14">
        {services.map((s, i) => (
          <article key={s.title} className="flow-step border-t-2 border-foreground pt-6" style={{ "--i": Math.min(i, 5) } as React.CSSProperties}>
            <div className="flex items-start gap-3 mb-3">
              <s.icon size={22} className="mt-1 shrink-0 text-primary" aria-hidden="true" />
              <h3 className="font-display text-xl font-bold text-foreground">{s.title}</h3>
            </div>
            <p className="text-base text-muted-foreground leading-relaxed mb-5">{s.description}</p>
            <ul className="divide-y divide-border border-y">
              {s.features.map((f) => (
                <li key={f} className="py-2.5 text-base text-foreground">{f}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>

      {/* Multiplatform Coverage */}
      <div className="card-surface p-6 md:p-8 mb-6">
        <h3 className="font-display text-xl font-bold text-foreground mb-2">Multiplatform Coverage</h3>
        <p className="text-base text-muted-foreground mb-6">One engagement. Complete coverage.</p>
        <div className="grid sm:grid-cols-3 gap-4" role="list">
          {platforms.map((p) => (
            <div key={p.label} className="flex items-center gap-3 bg-muted/50 rounded-xl p-4" role="listitem">
              <p.icon size={20} className="text-primary shrink-0" aria-hidden="true" />
              <span className="text-base font-medium text-foreground">{p.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Why it matters */}
      <div className="card-surface p-6 md:p-8">
        <h3 className="font-display text-xl font-bold text-foreground mb-4">Why Accessibility Conformance Matters?</h3>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {whyMatters.map((w) => (
            <li key={w} className="flex items-start gap-2 text-base text-foreground">
              <Check size={14} className="text-primary shrink-0 mt-0.5" aria-hidden="true" /> {w}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
  );
};

export default ServicesSection;
